import { resolveConflict, type MergeResult } from '../../domain/merge';
import { applyCommand, createTicket } from '../../domain/state-machine';
import type { CoupleSpace, Ticket, TicketCommand, TicketDraft } from '../../domain/types';
import { fromBase64Url } from '../../protocol/base64url';
import { decodeMessageLink, encodeMessageLink } from '../../protocol/links';
import type { AppRepositoryContract } from '../../storage/repository';

export interface IncomingTicketPreview {
  kind: 'preview';
  importId: string;
  ticket: Ticket;
}

export type IncomingTicketResult =
  | { kind: 'applied' | 'duplicate' | 'stale'; ticket: Ticket; shareUrl: string }
  | { kind: 'conflict'; importId: string; local: Ticket; incoming: Ticket };

export class TicketService {
  private readonly pendingImports = new Map<string, Ticket>();
  private readonly pendingConflicts = new Map<string, Extract<MergeResult, { kind: 'conflict' }>>();
  private readonly resolvingConflicts = new Set<string>();

  constructor(
    private readonly repo: AppRepositoryContract,
    private readonly baseUrl: string,
  ) {}

  async create(draft: TicketDraft, at: string): Promise<{ ticket: Ticket; shareUrl: string }> {
    const space = await requireSpace(this.repo);
    const ticket = createTicket(draft, space.coupleId, space.memberId, at, crypto.randomUUID());
    const shareUrl = await this.share(ticket, space);
    await this.repo.saveTicket(ticket);
    return {
      ticket,
      shareUrl,
    };
  }

  async createWithImage(
    draft: TicketDraft,
    imageId: string,
    image: Blob,
    at: string,
  ): Promise<{ ticket: Ticket; shareUrl: string }> {
    const space = await requireSpace(this.repo);
    const ticket = createTicket({ ...draft, imageId }, space.coupleId, space.memberId, at, crypto.randomUUID());
    const shareUrl = await this.share(ticket, space);
    await this.repo.saveTicketWithImage(ticket, imageId, image);
    return { ticket, shareUrl };
  }

  async act(ticketId: string, command: TicketCommand, at: string): Promise<{ ticket: Ticket; shareUrl: string }> {
    const space = await requireSpace(this.repo);
    if (command.actor !== space.memberId) throw new Error('操作身份与本机固定身份不一致');
    const current = await requireTicket(this.repo, ticketId);
    if (current.coupleId !== space.coupleId) throw new Error('这张单不属于当前双人空间');
    const ticket = applyCommand(current, command, at, crypto.randomUUID());
    const shareUrl = await this.share(ticket, space);
    await this.repo.saveTicket(ticket);
    return {
      ticket,
      shareUrl,
    };
  }

  async applyIncoming(token: string): Promise<IncomingTicketPreview> {
    const space = await requireSpace(this.repo);
    const payload = await decodeMessageLink(token, decodeSpaceKey(space));
    if (payload.kind !== 'ticket') throw new Error('这条链接不是单据更新');
    if (payload.ticket.coupleId !== space.coupleId) throw new Error('这条链接属于另一个双人空间');
    const importId = token;
    this.pendingImports.set(importId, payload.ticket);
    return { kind: 'preview', importId, ticket: payload.ticket };
  }

  async confirmIncoming(importId: string): Promise<IncomingTicketResult> {
    const incoming = this.pendingImports.get(importId);
    if (!incoming) throw new Error('请重新打开链接并确认单据内容');
    const space = await requireSpace(this.repo);
    if (incoming.coupleId !== space.coupleId) throw new Error('这条链接属于另一个双人空间');

    const result = await this.repo.importTicket(incoming, importId);
    if (result.kind === 'conflict') {
      this.pendingConflicts.set(importId, result);
      return { ...result, importId };
    }
    this.pendingImports.delete(importId);

    const ticket = result.kind === 'duplicate'
      ? await this.repo.getTicket(incoming.id) ?? incoming
      : result.ticket;
    return {
      kind: result.kind,
      ticket,
      shareUrl: await this.share(ticket, space),
    };
  }

  async resolveIncomingConflict(
    importId: string,
    keep: 'local' | 'incoming',
    at: string,
  ): Promise<{ ticket: Ticket; shareUrl: string }> {
    if (this.resolvingConflicts.has(importId)) throw new Error('这条冲突正在处理，请勿重复提交');
    this.resolvingConflicts.add(importId);
    try {
      const conflict = this.pendingConflicts.get(importId);
      if (!conflict) throw new Error('找不到待处理的冲突，请重新打开链接');
      const space = await requireSpace(this.repo);
      const ticket = resolveConflict(conflict, keep, space.memberId, at, crypto.randomUUID());
      const shareUrl = await this.share(ticket, space);
      await this.repo.saveTicket(ticket, importId);
      this.pendingConflicts.delete(importId);
      this.pendingImports.delete(importId);
      return { ticket, shareUrl };
    } finally {
      this.resolvingConflicts.delete(importId);
    }
  }

  private share(ticket: Ticket, space: CoupleSpace): Promise<string> {
    return encodeMessageLink(this.baseUrl, { kind: 'ticket', ticket }, decodeSpaceKey(space));
  }
}

async function requireSpace(repo: AppRepositoryContract): Promise<CoupleSpace> {
  const space = await repo.getSpace();
  if (!space) throw new Error('请先完成双人空间配对');
  return space;
}

async function requireTicket(repo: AppRepositoryContract, ticketId: string): Promise<Ticket> {
  const ticket = await repo.getTicket(ticketId);
  if (!ticket) throw new Error('找不到这张单');
  return ticket;
}

function decodeSpaceKey(space: CoupleSpace): Uint8Array {
  const key = fromBase64Url(space.key);
  if (key.length !== 32) throw new Error('双人空间密钥无效');
  return key;
}
